import { bilanDeLaSemaine, semaineOrganisee, tourneeDuChapitre } from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * La fiche technique du chapitre 4, celle qui part dans le cahier.
 *
 * Elle montre les trois sorties telles que l'îlot les a organisées, puis la
 * comparaison avec la tournée fixe. C'est cette comparaison qui porte la
 * compétence C4 : la même ville, la même semaine, et quatre fois moins de
 * kilomètres parce qu'une donnée a remplacé une habitude.
 */
export function FicheCollecte({
  content,
  partie,
  onEcrireTrace,
  onFermer,
}: {
  content: Content;
  partie: GameState;
  onEcrireTrace: (fieldId: string, valeur: string) => void;
  onFermer: () => void;
}) {
  const chapitre = content.chapitres.find((c) => c.id === 'ch4');
  const tournee = tourneeDuChapitre(content, 'ch4');
  const bilan = bilanDeLaSemaine(partie, content);

  const champ = (id: string) => chapitre?.trace_ecrite.champs.find((f) => f.id === id);
  const champChemin = champ('chemin_donnee');
  const champLimites = champ('limites');

  return (
    <article className="rounded-2xl border-2 border-slate-300 bg-white p-5 print:border-0 print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{chapitre?.trace_ecrite.titre}</h2>
          <p className="text-sm text-slate-600">
            {T.rapport.ilot} {partie.islandCode}
          </p>
        </div>

        <div className="flex gap-2 print:hidden">
          <button
            type="button"
            onClick={() => globalThis.print()}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.rapport.imprimer}
          </button>
          <button
            type="button"
            onClick={onFermer}
            className="min-h-touch rounded-xl bg-slate-100 px-4 font-semibold text-slate-700"
          >
            {T.rapport.fermer}
          </button>
        </div>
      </header>

      {tournee && semaineOrganisee(partie, content) && (
        <section className="mt-5">
          <h3 className="font-bold text-slate-900">{T.fiche4.tournees}</h3>
          <ul className="mt-2 space-y-1">
            {bilan.jours.map((jour) => (
              <li key={jour.jour} className="text-slate-800">
                <span className="font-semibold capitalize">{jour.jour}</span> —{' '}
                {jour.conteneurs.filter((c) => c.collecte).length} {T.tournee.arretsChoisis},{' '}
                <span className="tabular-nums">{Math.round(jour.cout.km)}</span>{' '}
                {T.bilanCollecte.km}
                {jour.conteneurs.some((c) => c.collecte) && (
                  <span className="text-slate-600">
                    {' '}
                    (
                    {jour.conteneurs
                      .filter((c) => c.collecte)
                      .map((c) => c.nom)
                      .join(', ')}
                    )
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {champChemin && (
        <section className="mt-5">
          <label htmlFor="trace-chemin" className="block font-bold text-slate-900">
            {champChemin.label}
          </label>
          <textarea
            id="trace-chemin"
            rows={2}
            value={partie.writtenTrace.ch4['chemin_donnee'] ?? ''}
            onChange={(evenement) => onEcrireTrace('chemin_donnee', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5">
        <h3 className="font-bold text-slate-900">{T.fiche4.bilan}</h3>
        <dl className="mt-2 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-slate-100 p-3">
            <dt className="text-sm text-slate-600">{T.fiche4.avant}</dt>
            <dd className="text-xl font-bold tabular-nums text-slate-900">
              {Math.round(bilan.actuelle.km)} {T.bilanCollecte.km}
            </dd>
            <dd className="text-sm text-slate-600">
              {Math.round(bilan.actuelle.kgCo2)} {T.bilanCollecte.kgCo2}
            </dd>
          </div>
          <div className="rounded-xl bg-sky-50 p-3">
            <dt className="text-sm text-slate-600">{T.fiche4.apres}</dt>
            <dd className="text-xl font-bold tabular-nums text-sky-900">
              {Math.round(bilan.km)} {T.bilanCollecte.km}
            </dd>
            <dd className="text-sm text-slate-600">
              {Math.round(bilan.kgCo2)} {T.bilanCollecte.kgCo2}
            </dd>
          </div>
          <div className="rounded-xl bg-emerald-50 p-3">
            <dt className="text-sm text-slate-600">{T.bilanCollecte.economie}</dt>
            <dd className="text-xl font-bold tabular-nums text-emerald-800">
              {Math.round(bilan.actuelle.km - bilan.km)} {T.bilanCollecte.km}
            </dd>
            <dd className="text-sm text-slate-600">
              {T.bilanCollecte.debordements} : {bilan.debordements}
            </dd>
          </div>
        </dl>
      </section>

      {champLimites && (
        <section className="mt-5">
          <label htmlFor="trace-limites" className="block font-bold text-slate-900">
            {champLimites.label}
          </label>
          <textarea
            id="trace-limites"
            rows={4}
            value={partie.writtenTrace.ch4['limites'] ?? ''}
            onChange={(evenement) => onEcrireTrace('limites', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5 rounded-xl bg-amber-50 p-3">
        <h3 className="font-bold text-slate-900">{T.rapport.aRetenir}</h3>
        <p className="mt-1 text-slate-800">{chapitre?.trace_ecrite.a_retenir}</p>
      </section>
    </article>
  );
}
